import Colors from '../../constants/Colors';
import { ChosenSlots, DateSlots } from '../../contexts/checkout';
import useColorScheme from '../../hooks/useColorScheme';
import { dateStringToNumeric } from '../../utils/date';
import { formatDateHoursRange } from '../../utils/locations';
import { TextBodySmall } from '../StyledText';

export default function DailyDatesPreview({
  chosenSlots,
  dateSlots,
}: {
  chosenSlots: ChosenSlots;
  dateSlots: DateSlots;
}) {
  const theme = useColorScheme();

  const data = getDailyDatesPreview(chosenSlots, dateSlots);

  return (
    <>
      {data.map((entry) => {
        if (!entry) {
          return null;
        }
        const { dateString, dayTime, label } = entry;
        return (
          <TextBodySmall
            key={dateString + dayTime}
            style={{ color: Colors[theme].textSecondary }}
          >
            {label}
          </TextBodySmall>
        );
      })}
    </>
  );
}

export function getDailyDatesPreview(
  chosenSlots: ChosenSlots,
  dateSlots: DateSlots
) {
  const slotsObjects = Object.keys(chosenSlots || {})
    .sort()
    .map((dateString) => {
      const daytimes = chosenSlots[dateString] || [];
      return daytimes.map((dayTime) => {
        const dateSlot = dateSlots[dateString];
        if (!dateSlot) return null;

        const slot = dateSlot[dayTime];
        if (!slot) return null;

        const { dropoff, pickup } = slot;

        return {
          dateString,
          dayTime,
          dropoff,
          pickup,
        };
      });
    })
    .flat();

  const slotsReduced = slotsObjects.reduce(
    (acc, cur) => {
      if (!cur) {
        return acc;
      }
      const { dateString, dropoff, pickup, dayTime } = cur;

      const matchingDropoff = acc[dropoff];

      if (!matchingDropoff) {
        acc[pickup] = { dropoff, pickup, dayTime: [dayTime], dateString };
      } else {
        acc[pickup] = {
          ...matchingDropoff,
          pickup,
          dayTime: [...matchingDropoff.dayTime, dayTime],
        };
        delete acc[dropoff];
      }

      return acc;
    },
    {} as Record<
      string,
      {
        dropoff: string;
        pickup: string;
        dateString: string;
        dayTime: string[];
      }
    >
  );

  const sessionsFormatted = Object.values(slotsReduced)
    .sort((a, b) => a.dateString.localeCompare(b.dateString))
    .map((s) => ({
      ...s,
      label: `${dateStringToNumeric(s.dateString)}, ${formatDateHoursRange(
        s.dropoff,
        s.pickup
      )}`,
    }));

  return sessionsFormatted;
}
